import React, { FC, useState } from 'react';
import { Box, Container, Divider, Grid, Theme, Typography, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { LinkBox } from 'components/atoms/expertise';
import { TextBlockWithLink, TextWithImageBlock } from 'components/structures/expertise';
import { TextWithImageBlockProps } from 'components/structures/expertise/TextWithImageBlock';
import { expertiseLinksList } from 'config/routes/routes';
import { NextRouter, useRouter } from 'next/dist/client/router';
import Link from 'next/link';
import { SmallWhiteArrowIcon } from '../../svgs/expertise';

interface DetailPageProps {
  listBlocks: Array<TextWithImageBlockProps>;
  text?: string;
  linkTitle?: string;
  link?: string;
}

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    root: {
      backgroundColor: theme.palette.background.default,
    },
    fixedBlock: {
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(7, 3),
      top: 118,
      marginBottom: theme.spacing(9),
      maxHeight: '200vh',
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(7),
        marginBottom: 0,
      },
    },
    fixedDesktopBlock: {
      backgroundColor: theme.palette.common.white,
      top: 118,
      marginBottom: theme.spacing(9),
      maxHeight: '100vh',
    },
    title: {
      fontWeight: 500,
      color: theme.palette.primary.dark,
      marginBottom: theme.spacing(4),
    },
    divider: {
      height: 1,
      maxWidth: 370,
      backgroundColor: theme.palette.secondary.light,
      marginBottom: theme.spacing(4),
    },
    blockTitle: {
      fontWeight: 500,
      fontSize: 25,
      color: '#292C3D',
      lineHeight: '25px',
    },
    button: {
      backgroundColor: '#F5F5F5',
      padding: theme.spacing(3, 0),
      color: '#292C3D',
      cursor: 'pointer',
    },
    buttonActive: {
      backgroundColor: theme.palette.primary.main,
      padding: theme.spacing(3, 0),
      color: 'white',
    },
    menu: {
      backgroundColor: theme.palette.primary.main,
      padding: theme.spacing(3),
      color: 'white',
      cursor: 'pointer',
    },
    menuTitle: {
      fontWeight: 500,
      fontSize: 20,
    },
    menuSubtitle: {
      fontWeight: 500,
      fontSize: 20,
      color: '#292C3D',
      marginBottom: theme.spacing(2),
      cursor: 'pointer',
    },
    menuActiveSubtitle: {
      fontWeight: 600,
      fontSize: 20,
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(2),
    },
    link: {
      cursor: 'pointer',
    },
    menuText: {
      fontSize: 15,
      color: '#131313',
      marginBottom: theme.spacing(2),
      cursor: 'pointer',
    },
  });
});

const DetailPage: FC<DetailPageProps> = ({ listBlocks, text, link, linkTitle }) => {
  const classes = useStyles();
  const isMedium = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const isLarge = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const { asPath }: NextRouter = useRouter();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(true);

  const cleanPath = (asPath: string) => {
    if (asPath.includes('#')) {
      return asPath.split('#')[0];
    }
    return asPath;
  };

  const cleanedPath = cleanPath(asPath);

  const matchedItem = expertiseLinksList.find(
    (item) => item.route === cleanedPath || item.subtitleHref === cleanedPath || item.secondSubtitleHref === cleanedPath,
  );

  const handleClickSubMenu = (item: any) => {
    router.push(`#${item}`);
  };

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Box pt={isMedium ? 11 : 10} pb={isMedium ? 30 : 19} className={classes.root}>
      <Container fixed>
        {isMedium && (
          <Box bgcolor={'white'} mb={6} p={4}>
            <Box display="flex" justifyContent="space-between" mb={5}>
              <Divider
                style={{
                  marginTop: 13,
                  backgroundColor: '#E0621E',
                  height: '1px',
                  width: `${isLarge ? '43%' : '42%'}`,
                }}
              />
              <Typography className={classes.blockTitle}> {'Expertise'} </Typography>
              <Divider
                style={{
                  marginTop: 13,
                  backgroundColor: '#E0621E',
                  height: '1px',
                  width: `${isLarge ? '43%' : '42%'}`,
                }}
              />
            </Box>
            <Grid container direction="row" justifyContent="flex-start" spacing={2}>
              {expertiseLinksList.map((item, index) => {
                return (
                  <Grid xs={12} md={3} lg={3} key={index} item>
                    <Link href={item.route}>
                      <Box
                        display={'flex'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        textAlign={'center'}
                        fontSize={15}
                        fontWeight={500}
                        className={
                          item.route === cleanedPath ||
                          item.subtitleHref === cleanedPath ||
                          item.secondSubtitleHref === cleanedPath
                            ? classes.buttonActive
                            : classes.button
                        }
                      >
                        {item.hasAccordion ? item.mainTitle : item.label}
                      </Box>
                    </Link>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        <Grid container direction="row" justifyContent="space-between" spacing={6}>
          {isMedium ? (
            <Grid xs={12} lg={4} item data-aos="fade-up">
              <Box className={classes.fixedDesktopBlock} position={isMedium ? 'sticky' : 'inherit'} maxHeight="100vh">
                <Box
                  className={classes.menu}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                  display={'flex'}
                  onClick={handleClick}
                >
                  <Typography className={classes.menuTitle}>
                    {matchedItem?.hasAccordion ? matchedItem?.mainTitle : matchedItem?.label}
                  </Typography>
                  <SmallWhiteArrowIcon
                    style={{
                      transform: isOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                      transition: 'transform 0.3s ease',
                    }}
                  />
                </Box>
                {isOpen && (
                  <Box px={3} py={4}>
                    {matchedItem?.hasAccordion ? (
                      <>
                        <Box>
                          <Link href={matchedItem.route}>
                            <Typography
                              className={cleanedPath === matchedItem.route ? classes.menuActiveSubtitle : classes.menuSubtitle}
                            >
                              {matchedItem.label}
                            </Typography>
                          </Link>

                          {cleanedPath === matchedItem.route && (
                            <>
                              {listBlocks.map((item, index) => (
                                <Typography onClick={() => handleClickSubMenu(item.id)} key={index} className={classes.menuText}>
                                  {item.title}
                                </Typography>
                              ))}
                              <Divider
                                style={{
                                  marginTop: 13,
                                  backgroundColor: '#E0621E',
                                  height: '1px',
                                  width: `100%`,
                                  marginBottom: '15px',
                                }}
                              />
                            </>
                          )}
                        </Box>

                        {!!matchedItem.subtitle && (
                          <Box>
                            <Link href={matchedItem.subtitleHref}>
                              <Typography
                                className={
                                  cleanedPath === matchedItem.subtitleHref ? classes.menuActiveSubtitle : classes.menuSubtitle
                                }
                              >
                                {matchedItem.subtitle}
                              </Typography>
                            </Link>
                            {cleanedPath === matchedItem.subtitleHref && (
                              <>
                                {listBlocks.map((item, index) => (
                                  <Typography
                                    onClick={() => handleClickSubMenu(item.id)}
                                    key={index}
                                    className={classes.menuText}
                                  >
                                    {item.title}
                                  </Typography>
                                ))}
                                <Divider
                                  style={{
                                    marginTop: 13,
                                    backgroundColor: '#E0621E',
                                    height: '1px',
                                    width: `100%`,
                                    marginBottom: '15px',
                                  }}
                                />
                              </>
                            )}
                          </Box>
                        )}

                        {!!matchedItem.secondSubtitle && (
                          <Box>
                            <Link href={matchedItem.secondSubtitleHref}>
                              <Typography
                                className={
                                  cleanedPath === matchedItem.secondSubtitleHref
                                    ? classes.menuActiveSubtitle
                                    : classes.menuSubtitle
                                }
                              >
                                {matchedItem.secondSubtitle}
                              </Typography>
                            </Link>
                            {cleanedPath === matchedItem.secondSubtitleHref && (
                              <>
                                {listBlocks.map((item, index) => (
                                  <Typography
                                    onClick={() => handleClickSubMenu(item.id)}
                                    key={index}
                                    className={classes.menuText}
                                  >
                                    {item.title}
                                  </Typography>
                                ))}
                                <Divider
                                  style={{
                                    marginTop: 13,
                                    backgroundColor: '#E0621E',
                                    height: '1px',
                                    width: `100%`,
                                  }}
                                />
                              </>
                            )}
                          </Box>
                        )}
                      </>
                    ) : (
                      <>
                        {listBlocks.map((item, index) => (
                          <Typography onClick={() => handleClickSubMenu(item.id)} key={index} className={classes.menuText}>
                            {item.title}
                          </Typography>
                        ))}
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </Grid>
          ) : (
            <Grid xs={12} lg={4} item data-aos="fade-up">
              <Box className={classes.fixedBlock} position={isMedium ? 'sticky' : 'inherit'} maxHeight="104vh">
                <Typography variant="h5" className={classes.title}>
                  Expertise
                </Typography>
                <Divider className={classes.divider} />
                {expertiseLinksList.map(
                  (
                    { label, route, hasAccordion, mainTitle, subtitleHref, subtitle, secondSubtitle, secondSubtitleHref },
                    index,
                  ) => (
                    <LinkBox
                      title={label}
                      href={route}
                      key={index}
                      active={asPath === route}
                      hasAccordion={hasAccordion}
                      mainTitle={mainTitle}
                      subtitleHref={subtitleHref}
                      activeSubtitle={asPath === subtitleHref}
                      activeSecondSubtitle={asPath === secondSubtitleHref}
                      subtitle={subtitle}
                      secondSubtitle={secondSubtitle}
                      secondSubtitleHref={secondSubtitleHref}
                    />
                  ),
                )}
              </Box>
            </Grid>
          )}
          {!!listBlocks?.length && (
            <Grid xs={12} lg={8} item data-aos="fade-up" data-aos-delay="300">
              {listBlocks.map((item, index) => (
                <Box key={index}>
                  <TextWithImageBlock
                    description={item.description}
                    title={item.title}
                    listItems={item.listItems}
                    bgImage={item.bgImage}
                    hasListItems={item.hasListItems}
                    listAccordionItems={item.listAccordionItems}
                    id={item.id}
                    textTitle={item.textTitle}
                    descriptionHTML={item.descriptionHTML}
                  />
                </Box>
              ))}
              {!!text && <TextBlockWithLink text={text} link={link} linkTitle={linkTitle} />}
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default DetailPage;
